import React, { useState, useEffect } from 'react';
import { adminApiService } from '../../services/adminApi';
import { useAdmin } from '../../hooks/useAdmin';
import styles from '../../styles/admin/WelcomeAirdropManager.module.css';
import { ethers } from 'ethers';
import { appKitBlockchainService } from '../../services/appkitBlockchain';
import { WelcomeAirdropSubmission } from '../../types';

const WelcomeAirdropManager: React.FC = () => {
  const { isAdmin } = useAdmin();
  const [submissions, setSubmissions] = useState<WelcomeAirdropSubmission[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [telegramMessage, setTelegramMessage] = useState<string | null>(null);

  console.log("telegramMessage", telegramMessage);
  
  // Form state for creating new submission
  const [newSubmission, setNewSubmission] = useState({
    walletAddress: '',
    xAccount: '',
    telegramUsername: '',
    dxfAmount: '100'
  });
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'processed'>('all');
  
  // Copy to clipboard helper
  const copyToClipboard = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      return true;
    }
  };

  // Load submissions
  const loadSubmissions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const filterParam = statusFilter === 'all' ? undefined : statusFilter;
      const response = await adminApiService.getWelcomeAirdropSubmissions(filterParam);
      setSubmissions(response.data || []);
    } catch (err: any) {
      setError(`Failed to load submissions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      loadSubmissions();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, statusFilter]);

  // Create new submission
  const handleCreateSubmission = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newSubmission.walletAddress || !newSubmission.xAccount || !newSubmission.telegramUsername) {
      setError('All fields are required');
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      await adminApiService.createWelcomeAirdropSubmission({
        walletAddress: newSubmission.walletAddress.toLowerCase(),
        xAccount: newSubmission.xAccount,
        telegramUsername: newSubmission.telegramUsername,
        dxfAmount: newSubmission.dxfAmount
      });
      
      setSuccess('Submission created successfully');
      setNewSubmission({
        walletAddress: '',
        xAccount: '',
        telegramUsername: '',
        dxfAmount: '100'
      });
      
      // Reload submissions
      loadSubmissions();
    } catch (err: any) {
      setError(`Failed to create submission: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Process pending submissions
  const handleProcessSubmissions = async () => {
    const pendingSubmissions = submissions.filter(s => s.status === 'pending');
    
    if (pendingSubmissions.length === 0) {
      setError('No pending submissions to process');
      return;
    }

    const confirmed = window.confirm(
      `Process ${pendingSubmissions.length} pending submission(s)? This will execute blockchain transactions.`
    );
    
    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await adminApiService.processPendingWelcomeAirdropSubmissions();
      
      if (response.processed > 0) {
        setSuccess(`Successfully processed ${response.processed} submission(s). TX Hash: ${response.txHash}`);
        // Generate Telegram message
        const explorerLink = appKitBlockchainService.explorerTx(response.txHash);
        const telegramUsernames = response.recipients.map(r => r.telegramUsername).join(' ');
        
        const message = `**Welcome Airdrop - $DXF tokens delivered!**
 
Verify your loot on BscScan: ${explorerLink}

Now, go use them on https://deathx.fun and dominate!
Tips: Holding $DXF tokens makes you eligible for the daily draw for free!

${telegramUsernames}`;
        
        setTelegramMessage(message);
      } else {
        setError('No submissions were processed');
      }
      
      // Reload submissions
      loadSubmissions();
    } catch (err: any) {
      setError(`Failed to process submissions: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const pendingCount = (submissions || []).filter(s => s.status === 'pending').length;
  const processedCount = (submissions || []).filter(s => s.status === 'processed').length;

  if (!isAdmin) {
    return (
      <div className={styles.accessDenied}>
        <h3>Access Denied</h3>
        <p>Admin privileges required to manage welcome airdrops.</p>
      </div>
    );
  }

  return (
    <div className={styles.airdropManager}>
      <div className={styles.managerHeader}>
        <h2>🎁 Welcome Airdrop Manager</h2>
        <p>Manage welcome airdrop submissions and process rewards</p>
      </div>

      {/* Status Messages */}
      {error && (
        <div className={styles.errorMessage}>
          <span className={styles.errorIcon}>❌</span>
          {error}
        </div>
      )}
      
      {success && (
        <div className={styles.successMessage}>
          <span className={styles.successIcon}>✅</span>
          {success}
        </div>
      )}
      
      {telegramMessage && (
        <div className={styles.telegramMessage}>
          <h3>📱 Telegram Message for Community Group</h3>
          <p>Copy and send this message to the Telegram community group:</p>
          <div className={styles.messageContainer}>
            <pre className={styles.messageContent}>{telegramMessage}</pre>
            <button
              onClick={async () => {
                const copied = await copyToClipboard(telegramMessage);
                if (copied) {
                  setSuccess('Telegram message copied to clipboard!');
                  setTimeout(() => setSuccess(null), 3000);
                }
              }}
              className={`${styles.btn} ${styles.btnSecondary}`}
            >
              📋 Copy Message
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className={styles.statsContainer}>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{submissions.length}</div>
          <div className={styles.statLabel}>Total Submissions</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{pendingCount}</div>
          <div className={styles.statLabel}>Pending</div>
        </div>
        <div className={styles.statCard}>
          <div className={styles.statNumber}>{processedCount}</div>
          <div className={styles.statLabel}>Processed</div>
        </div>
      </div>

      {/* Actions */}
      <div className={styles.actionsContainer}>
        <button
          onClick={handleProcessSubmissions}
          disabled={isLoading || pendingCount === 0}
          className={`${styles.btn} ${styles.btnPrimary}`}
        >
          {isLoading ? 'Processing...' : `Process ${pendingCount} Pending Submissions`}
        </button>
        <button
          onClick={loadSubmissions}
          disabled={isLoading}
          className={`${styles.btn} ${styles.btnSecondary}`}
        >
          {isLoading ? 'Loading...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Create New Submission */}
      <div className={styles.createSection}>
        <h3>📝 Create New Submission</h3>
        <form onSubmit={handleCreateSubmission} className={styles.createForm}>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Wallet Address:</label>
              <input
                type="text"
                value={newSubmission.walletAddress}
                onChange={(e) => setNewSubmission({...newSubmission, walletAddress: e.target.value})}
                placeholder="0x..."
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>X Account:</label>
              <input
                type="text"
                value={newSubmission.xAccount}
                onChange={(e) => setNewSubmission({...newSubmission, xAccount: e.target.value})}
                placeholder="X Username"
                required
              />
            </div>
          </div>
          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label>Telegram Username:</label>
              <input
                type="text"
                value={newSubmission.telegramUsername}
                onChange={(e) => setNewSubmission({...newSubmission, telegramUsername: e.target.value})}
                placeholder="TG Username"
                required
              />
            </div>
            <div className={styles.formGroup}>
              <label>DXF Amount:</label>
              <input
                type="number"
                value={newSubmission.dxfAmount}
                onChange={(e) => setNewSubmission({...newSubmission, dxfAmount: e.target.value})}
                min="1"
                max="1000"
                required
              />
            </div>
          </div>
          <button type="submit" disabled={isLoading} className={`${styles.btn} ${styles.btnPrimary}`}>
            {isLoading ? 'Creating...' : '➕ Create Submission'}
          </button>
        </form>
      </div>

      {/* Filter */}
      <div className={styles.filterSection}>
        <label>Filter by status:</label>
        <select 
          value={statusFilter} 
          onChange={(e) => setStatusFilter(e.target.value as any)}
          className={styles.filterSelect}
        >
          <option value="all">All Submissions</option>
          <option value="pending">Pending Only</option>
          <option value="processed">Processed Only</option>
        </select>
      </div>

      {/* Submissions List */}
      <div className={styles.submissionsList}>
        <h3>📋 Submissions ({submissions.length})</h3>
        
        {isLoading ? (
          <div className={styles.loading}>Loading submissions...</div>
        ) : submissions.length === 0 ? (
          <div className={styles.noSubmissions}>
            No submissions found {statusFilter !== 'all' && `for status: ${statusFilter}`}
          </div>
        ) : (
          <div className={styles.submissionsTable}>
            <div className={styles.tableHeader}>
              <div>Wallet</div>
              <div>X Account</div>
              <div>Telegram</div>
              <div>DXF Amount</div>
              <div>Status</div>
              <div>Created</div>
              <div>TX Hash</div>
            </div>
            
            {submissions.map((submission) => (
              <div key={submission.id} className={styles.tableRow}>
                <div className={styles.walletCell}>
                  {submission.walletAddress.slice(0, 6)}...{submission.walletAddress.slice(-4)}
                </div>
                <div>{submission.xAccount}</div>
                <div>{submission.telegramUsername}</div>
                <div>{ethers.formatUnits(submission.dxfAmount)} DXF</div>
                <div>
                  <span className={`${styles.statusBadge} ${styles[submission.status]}`}>
                    {submission.status}
                  </span>
                </div>
                <div className={styles.dateCell}>
                  {new Date(submission.createdAt).toLocaleDateString()}
                </div>
                <div className={styles.txHashCell}>
                  {submission.txHash ? (
                    <a 
                      href={appKitBlockchainService.explorerTx(submission.txHash)}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className={styles.txLink}
                    >
                      {submission.txHash.slice(0, 8)}...
                    </a>
                  ) : (
                    '-'
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeAirdropManager;